import { CommonModule } from '@angular/common';
import { Component, inject, type OnInit, signal, type WritableSignal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgxFloatUiModule, NgxFloatUiPlacements, NgxFloatUiTriggers } from 'ngx-float-ui';
import { filter, switchMap } from 'rxjs';

import type { TermAndCondition } from '@core/models/app.model';
import { NotifyService } from '@core/services/notifyService.service';
import { getParamFromRouteOrParent } from '@helpers/get-param-from-route-or-parent';
import { DIALOG_SIZE } from '@shared/constants/ume-constants';
import { SanitizeSrcPipe } from '@shared/pipes/sanitize-src.pipe';
import { PlainHtmlComponent } from '@widgets/plain-html/plain-html.component';

import { SubscribeOfferingDialogComponent } from '../../../dialogs/subscribe-offering-dialog/subscribe-offering-dialog.component';
import {
  type ICompanyServiceOffering,
  SERVICE_LOGICAL_STATUS,
} from '../../../models/services.model';
import { ServiceLogicalStatusPipe } from '../../../pipes/service-logical-status.pipe';
import { ServicesManagementService } from '../../../services/services-configuration.service';
import { OfferingTermsAndConditionsListComponent } from '../../../shared/offering-terms-and-conditions-list/offering-terms-and-conditions-list.component';

@UntilDestroy()
@Component({
  selector: 'ume-company-service-configuration',
  templateUrl: './company-service-configuration.component.html',
  styleUrls: ['./company-service-configuration.component.scss'],
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    MatButtonModule,
    NgxFloatUiModule,
    ServiceLogicalStatusPipe,
    SanitizeSrcPipe,
    PlainHtmlComponent,
    OfferingTermsAndConditionsListComponent,
  ],
})
export class CompanyServiceConfigurationPageComponent implements OnInit {
  readonly serviceLogicalStatuses = SERVICE_LOGICAL_STATUS;
  readonly popTriggers = NgxFloatUiTriggers;
  readonly popPlacements = NgxFloatUiPlacements;

  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly servicesManagementService: ServicesManagementService =
    inject(ServicesManagementService);
  private readonly notify: NotifyService = inject(NotifyService);
  private readonly dialog: MatDialog = inject(MatDialog);

  public service: WritableSignal<ICompanyServiceOffering | null> = signal(null);
  public terms: WritableSignal<TermAndCondition[]> = signal([]);
  public canEdit = false;
  public offeringId: string | null = getParamFromRouteOrParent(this.route.snapshot, 'offeringId');

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe((data) => {
      this.service.set(data.serviceSetting);
      this.terms.set(data.terms);
    });
  }

  public activateService(): void {
    if (!this.offeringId) {
      return;
    }

    const _offeringId = this.offeringId;

    this.dialog
      .open(SubscribeOfferingDialogComponent, {
        data: {
          title: `Subscribe to ${this.service()?.name}`,
          terms: this.terms(),
        },
        maxWidth: DIALOG_SIZE.MODAL_MD,
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.servicesManagementService.subscribeToService(_offeringId)),
        switchMap(() => this.servicesManagementService.getCompanyService(_offeringId)),
        untilDestroyed(this)
      )
      .subscribe((service: ICompanyServiceOffering) => {
        this.service.set(service);
        this.notify.notifySuccess('Service has been requested');
      });
  }
}
